import { Badge } from '@mantine/core';
import { formatPrice } from '../../utils/formatPrice';

export default function OrderTotalsSummary({ totals, t }) {
  if (!totals) return null;
  const {
    grossSubtotal,
    manualDiscount,
    ruleDiscount,
    volumeDiscountPercentage,
    cartDiscountPercent,
    creditsExceedTotal,
    freeItems,
    shippingFree,
    hasShippingFree,
    isPickup,
    vatAmount,
    appliedBalance,
    roundedTotal,
    roundOffDiff,
    displaySubtotal,
    displayVolume,
    displayCartDiscount,
    displayRefund,
    displayShipping,
    showVatRow,
  } = totals;

  const hasRule = Number(ruleDiscount) > 0;
  const hasVolume = Number(displayVolume) > 0;
  const hasCart = Number(cartDiscountPercent) > 0 && Number(displayCartDiscount) > 0;
  const hasRefund = Number(displayRefund) < 0;
  const balanceAmount = Number(appliedBalance) || 0;
  const sub = Math.max(1, Number(grossSubtotal || 0) - Number(manualDiscount || 0));
  const rulePct = hasRule ? Math.ceil((Number(ruleDiscount) / sub) * 100) : 0;

  return (
    <div className="take-order-totals" aria-live="polite">
      <div className="take-order-totals-row">
        <span>{t('takeOrderSubtotal', 'Subtotal')}</span>
        <span>{formatPrice(displaySubtotal)}</span>
      </div>
      {hasRule && (
        <div className="take-order-totals-row">
          <span>{t('offerDiscount', 'Offer Discount')}</span>
          <span className="discount-value">−{rulePct}%</span>
        </div>
      )}
      {hasVolume && (
        <div className="take-order-totals-row">
          <span>
            {t('volumeDiscount', 'Monthly Vol. Discount')}
            {volumeDiscountPercentage != null ? ` (${Number(volumeDiscountPercentage)}%)` : ''}
          </span>
          <span className="discount-value">−{formatPrice(displayVolume)}</span>
        </div>
      )}

      <div className="take-order-totals-row">
        <span>{t('takeOrderShipping', 'Shipping')}</span>
        <span>
          {isPickup
            ? t('takeOrderPickup', 'Pickup')
            : shippingFree
              ? (
                  <Badge color="teal" variant="filled" size="sm">
                    {t('takeOrderFreeShipping', 'Free shipping')}
                  </Badge>
                )
              : hasShippingFree
                ? `${formatPrice(0)} — ${t('takeOrderFree', 'free')}`
                : formatPrice(displayShipping)}
        </span>
      </div>
      {isPickup && (
        <div className="take-order-totals-row">
          <span className="take-order-flag take-order-flag--pickup">{t('takeOrderPickup', 'Pickup')}</span>
          <span />
        </div>
      )}

      {hasCart && (
        <div className="take-order-totals-row">
          <span>
            {t('cartDiscount', 'Cart Discount')}
            {Number(cartDiscountPercent) ? ` (${Number(cartDiscountPercent)}%)` : ''}
          </span>
          <span className="discount-value">−{formatPrice(displayCartDiscount)}</span>
        </div>
      )}
      {hasRefund && (
        <div className="take-order-totals-row">
          <span>{t('refund', 'Refund')}</span>
          <span className="take-order-flag take-order-flag--refund">−{formatPrice(Math.abs(Number(displayRefund)))}</span>
        </div>
      )}
      {creditsExceedTotal && (
        <div className="take-order-totals-row" role="alert">
          <span>{t('takeOrderRefundsExceedTotal', 'Refunds exceed the order total — reduce the refund or the cart discount.')}</span>
          <span />
        </div>
      )}

      {Array.isArray(freeItems) && freeItems.length > 0 && (
        <div className="take-order-free-items">
          {freeItems.map((fi, idx) => (
            <div className="take-order-free-items__row" key={idx}>
              {fi.quantity} × {fi.name || fi.reasonParams?.item || ''}{' '}
              <span className="discount-value">{t('takeOrderFree', 'free')}</span>
              {fi.reasonKey ? (
                <span className="take-order-free-items__reason">
                  {t(fi.reasonKey, '', fi.reasonParams || {})}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {/* Business (VAT-exclusive) shows VAT as a row; retail shows it as a footnote. */}
      {showVatRow && Number(vatAmount) > 0 && (
        <div className="take-order-totals-row">
          <span>{t('vat', 'VAT')}</span>
          <span>{formatPrice(vatAmount)}</span>
        </div>
      )}

      {balanceAmount > 0 && (
        <div className="take-order-totals-row">
          <span>{t('accountCreditApplied', 'Account Credit Applied')}</span>
          <span className="discount-value">−{formatPrice(balanceAmount)}</span>
        </div>
      )}
      {balanceAmount < 0 && (
        <div className="take-order-totals-row">
          <span>{t('previousBalanceDue', 'Previous Balance Due')}</span>
          <span className="take-order-flag take-order-flag--balance">+{formatPrice(Math.abs(balanceAmount))}</span>
        </div>
      )}

      {Math.round(roundOffDiff * 100) !== 0 && (
        <div className="take-order-totals-row">
          <span>{t('takeOrderRounding', 'Rounding (5 Rappen)')}</span>
          <span>{roundOffDiff < 0 ? '−' : '+'}{formatPrice(Math.abs(roundOffDiff))}</span>
        </div>
      )}
      <div className="take-order-totals-row take-order-totals-row--total">
        <span>{t('takeOrderTotalPayable', 'Total payable')}</span>
        <span>{formatPrice(roundedTotal)}</span>
      </div>
      {!showVatRow && Number(vatAmount) > 0 && (
        <div className="take-order-totals-row take-order-totals-row--vat-note">
          <span>* {t('takeOrderVat', 'VAT (incl.)')}</span>
          <span>{formatPrice(vatAmount)}</span>
        </div>
      )}
    </div>
  );
}